import { ReactNode, useState, useEffect } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { HelpCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

interface MetricCardProps {
  title: string;
  icon: ReactNode;
  value: string | number;
  description: string;
  isLoading?: boolean;
  hint?: string;
  variant?: "success" | "info" | "destructive" | "neutral";
}

const MetricCard = ({
  title,
  icon,
  value,
  description,
  hint,
  isLoading = false,
  variant = "neutral",
}: MetricCardProps) => {
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const [showMobileToast, setShowMobileToast] = useState(false);

  const showHintToast = () => {
    if (hint) {
      toast({
        title: title,
        description: hint,
        duration: 5000,
      });
      setShowMobileToast(false);
    }
  };

  const handleMobileTouch = () => {
    if (isMobile && hint) {
      setShowMobileToast(true);
      showHintToast();
    }
  };

  useEffect(() => {
    const handleClickOutside = () => {
      if (showMobileToast) {
        setShowMobileToast(false);
      }
    };

    if (isMobile && showMobileToast) {
      document.addEventListener("touchstart", handleClickOutside);
      return () =>
        document.removeEventListener("touchstart", handleClickOutside);
    }
  }, [showMobileToast, isMobile]);

  const HintIcon = () => {
    if (!hint) return null;

    if (isMobile) {
      return (
        <button
          onTouchStart={handleMobileTouch}
          className="text-brand-primary hover:text-brand-primary-hover transition-colors"
        >
          <HelpCircle className="h-4 w-4" />
        </button>
      );
    }

    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="text-brand-primary hover:text-brand-primary-hover transition-colors cursor-help">
              <HelpCircle className="h-4 w-4" />
            </div>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs">
            <p>{hint}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  const variantStyles = {
    success: "border-l-4 border-l-success",
    info: "border-l-4 border-l-info",
    destructive: "border-l-4 border-l-destructive",
    neutral: "border-l-4 border-l-muted-foreground/30",
  };

  const cardBgStyles = {
    success: "bg-success/5",
    info: "bg-info/5",
    destructive: "bg-destructive/5",
    neutral: "",
  };

  const iconBgStyles = {
    success: "bg-success/20 text-success",
    info: "bg-info/20 text-info",
    destructive: "bg-destructive/20 text-destructive",
    neutral: "bg-muted text-muted-foreground",
  };

  const valueStyles = {
    success: "text-success",
    info: "text-info",
    destructive: "text-destructive",
    neutral: "text-foreground",
  };

  return (
    <Card className={cn(
      "transition-all duration-200 hover:shadow-md hover:scale-[1.02]",
      variantStyles[variant],
      cardBgStyles[variant]
    )}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 p-4 pb-2">
        <div className="flex items-center gap-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
          <HintIcon />
        </div>
        <div className={cn(
          "h-10 w-10 rounded-full flex items-center justify-center",
          iconBgStyles[variant]
        )}>
          {icon}
        </div>
      </CardHeader>

      <CardContent className="p-4 pt-0">
        {isLoading ? (
          <Skeleton className="h-12 w-2/3" />
        ) : (
          <div className={cn("text-4xl font-bold tracking-tight", valueStyles[variant])}>{value}</div>
        )}

        <p className="text-xs text-muted-foreground mt-1">{description}</p>
      </CardContent>
    </Card>
  );
};

export default MetricCard;
